import type { ResponsiveNotePhoto } from "@rebuild/shared/models";
import { capitalizeWords } from "@rebuild/shared";
import { Download, Image, Loader2, Maximize2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useAnalytics } from "@/analytics";
import type { Estimation, File, FileToRoomMapping } from "@/interfaces/estimation.interface";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useReassignResponsiveNotePhotosMutation } from "@/hooks/mutation/responsive-note-photos/useReassignResponsiveNotePhotosMutation";
import { useApplyResponsiveNotePhotoLabelsMutation } from "@/hooks/mutation/responsive-note-photos/useApplyResponsiveNotePhotoLabelsMutation";
import { useDeleteResponsiveNotePhotosMutation } from "@/hooks/mutation/responsive-note-photos/useDeleteResponsiveNotePhotosMutation";
import { getInternalOrgOverride } from "@/redux/actions/app.actions";
import { isLabelablePhoto } from "./Photos/photoLabels.utils";
import type { ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import { MAX_PHOTOS_PER_DELETE, MAX_PHOTOS_PER_LABEL_APPLY } from "@rebuild/shared";
import { DeletePhotosDialog } from "./Photos/DeletePhotosDialog";
import { RoomPhotoGallery } from "./Photos/RoomPhotoGallery";
import { PhotoGallery } from "./Photos/PhotoGallery";
import { PhotoGalleryFullscreen } from "./Photos/PhotoGalleryFullscreen";
import { PhotoPreview } from "./Photos/PhotoPreview";
import { PhotoSelectionToolbar } from "./Photos/PhotoSelectionToolbar";
import type { PhotoGalleryItem } from "./Photos/photos.types";
import {
  boxForPrediction,
  buildPhotoGalleryGroups,
  buildPhotoDownloadNames,
  downloadPhotos,
  getEstimateFileRoomId,
  isPhotoGalleryItem,
  normalizeEstimateFilePhoto,
  normalizeResponsiveNotePhoto,
  sortEstimateFilesByRoomOrder,
  sortGroupPhotosByCapturedAt,
} from "./Photos/photos.utils";
import { EmptyTabState } from "./EmptyTabState";
import { Button } from "@/components/ui/button";

interface PhotosProps extends Pick<Estimation, "files"> {
  estimateId?: string;
  estimation?: Estimation;
  responsiveNotePhotos?: ResponsiveNotePhoto[];
  isResponsiveNotePhotosLoading?: boolean;
  hasResponsiveNotePhotosError?: boolean;
  photoPreviewOverlayRef?: RefObject<HTMLDivElement | null>;
  onFileSelect?: (file: File) => void;
  onFileClose?: () => void;
  hideRoomNames?: boolean;
  canExpand?: boolean;
}

export const Photos = ({
  estimateId,
  estimation,
  files,
  responsiveNotePhotos = [],
  isResponsiveNotePhotosLoading,
  hasResponsiveNotePhotosError,
  photoPreviewOverlayRef,
  onFileSelect,
  onFileClose,
  hideRoomNames,
  canExpand,
}: PhotosProps) => {
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | undefined>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenPreviewOverlay, setFullscreenPreviewOverlay] = useState<HTMLDivElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [isMoving, setIsMoving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [activeDownload, setActiveDownload] = useState<"selected" | "all" | null>(null);
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();
  const { mutateAsync: reassignResponsiveNotePhotos, isPending: isReassignPending } = useReassignResponsiveNotePhotosMutation();
  const { mutateAsync: applyPhotoLabels, isPending: isApplyLabelsPending } = useApplyResponsiveNotePhotoLabelsMutation();
  const { mutateAsync: deleteResponsiveNotePhotos, isPending: isDeletePending } = useDeleteResponsiveNotePhotosMutation();

  const selectPhoto = useCallback(
    (photo: PhotoGalleryItem | undefined) => {
      setSelectedPhotoId(photo?.galleryId);
      if (photo?.file && onFileSelect) {
        onFileSelect(photo.file);
      }
      if (!photo && onFileClose) {
        onFileClose();
      }
    },
    [onFileClose, onFileSelect],
  );

  const activeEstimateId = estimateId ?? estimation?.estimateId;
  const {
    currentRoom,
    rooms,
    lineItemDetail: storedLineItemDetail,
    rightSidebarCurrentTab,
    updateEstimate,
  } = useEstimateDetail(activeEstimateId!);
  const lineItemDetail = rightSidebarCurrentTab?.value === "Item" ? storedLineItemDetail : undefined;

  const lineItemKey = useMemo(
    () => `${lineItemDetail?.lineItem.lineItem || ""} --- ${lineItemDetail?.tuple.tupleId} -- ${lineItemDetail?.pageId}`,
    [lineItemDetail],
  );

  useEffect(() => {
    setSelectedPhotoId(undefined);
  }, [lineItemKey]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeEstimateId]);

  const filesSorted = useMemo(() => sortEstimateFilesByRoomOrder(files || [], rooms), [files, rooms]);

  const estimatePhotoItems = useMemo(() => normalizeFilePhotos(filesSorted), [filesSorted]);
  const responsiveNotePhotoItems = useMemo(() => responsiveNotePhotos.map(normalizeResponsiveNotePhoto), [responsiveNotePhotos]);
  const downloadFileNames = useMemo(() => buildPhotoDownloadNames(responsiveNotePhotos, rooms), [responsiveNotePhotos, rooms]);
  const photosTabItems = useMemo(
    () => [...estimatePhotoItems, ...responsiveNotePhotoItems],
    [estimatePhotoItems, responsiveNotePhotoItems],
  );
  const photosTabGroups = useMemo(
    () =>
      buildPhotoGalleryGroups({
        estimatePhotos: estimatePhotoItems,
        responsiveNotePhotos: responsiveNotePhotoItems,
        rooms,
      }),
    [estimatePhotoItems, responsiveNotePhotoItems, rooms],
  );
  const displayGroups = useMemo(() => sortGroupPhotosByCapturedAt(photosTabGroups), [photosTabGroups]);
  const previewPhotos = useMemo(() => displayGroups.flatMap((group) => group.photos), [displayGroups]);

  const roomOptions = useMemo(
    () =>
      rooms
        .filter((room) => !room.isUnmatchedCluster)
        .map((room) => ({
          value: room.dataForUI.metadata.pageId,
          label: room.dataForUI.metadata.roomName,
        })),
    [rooms],
  );

  const selectedPhotos = useMemo(() => photosTabItems.filter((photo) => selectedIds.has(photo.galleryId)), [photosTabItems, selectedIds]);

  // Legacy estimate files have no responsive-note record, so there is nothing to fetch for them.
  const downloadablePhotos = useMemo(() => photosTabItems.filter((photo) => photo.source === "responsive-note"), [photosTabItems]);

  const togglePhotoSelection = useCallback((photo: PhotoGalleryItem) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(photo.galleryId)) {
        next.delete(photo.galleryId);
      } else {
        next.add(photo.galleryId);
      }
      return next;
    });
  }, []);

  const handleSelectAllInGroup = useCallback((photos: PhotoGalleryItem[], select: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const photo of photos) {
        if (select) {
          next.add(photo.galleryId);
        } else {
          next.delete(photo.galleryId);
        }
      }
      return next;
    });
  }, []);

  const buildAnalyticsPayload = useCallback(
    (selectedPhotos: PhotoGalleryItem[], targetRoomId: string) => ({
      estimate_id: activeEstimateId || "",
      photo_count: selectedPhotos.length,
      photo_sources: Array.from(new Set(selectedPhotos.map((photo) => photo.source))),
      target_room_id: targetRoomId,
      from_unmatched: selectedPhotos.some((photo) => !photo.roomId),
    }),
    [activeEstimateId],
  );

  const movePhotosToRoom = useCallback(
    async (photosToMove: PhotoGalleryItem[], targetRoomId: string, targetRoomName: string) => {
      if (!activeEstimateId || photosToMove.length === 0) {
        return;
      }

      const analyticsPayload = buildAnalyticsPayload(photosToMove, targetRoomId);
      trackEvent(ANALYTICS_EVENTS.PHOTO_ROOM_REASSIGN_INITIATED, analyticsPayload);

      // Dismiss the selection toolbar immediately; photosToMove is already captured, and the
      // reassign mutation applies an optimistic update so photos move without waiting on the request.
      setSelectedIds(new Set());

      const responsiveNotePhotosToMove = photosToMove.filter((photo) => photo.source === "responsive-note");
      const estimateFilePhotosToMove = photosToMove.filter((photo) => photo.source === "estimate-file");

      try {
        setIsMoving(true);
        const operations: Promise<unknown>[] = [];

        if (responsiveNotePhotosToMove.length > 0) {
          operations.push(
            reassignResponsiveNotePhotos({
              estimateId: activeEstimateId,
              targetRoomId,
              photos: responsiveNotePhotosToMove.map((photo) => ({
                noteId: photo.responsiveNotePhoto!.noteId,
                photoId: photo.responsiveNotePhoto!.id,
              })),
            }),
          );
        }

        if (estimateFilePhotosToMove.length > 0) {
          const fileToRoomMappings: FileToRoomMapping[] = estimateFilePhotosToMove
            .filter((photo) => photo.file?.uploadId)
            .map((photo) => ({
              uploadId: photo.file!.uploadId!,
              roomId: targetRoomId,
              isMappedByUser: true,
            }));

          if (fileToRoomMappings.length > 0) {
            operations.push(updateEstimate({ fileToRoomMappings }));
          }
        }

        await Promise.all(operations);

        const movedCount = photosToMove.length;
        toast.success(`Moved ${movedCount} photo${movedCount === 1 ? "" : "s"} to "${capitalizeWords(targetRoomName)}"`);
        trackEvent(ANALYTICS_EVENTS.PHOTO_ROOM_REASSIGNED, analyticsPayload);
      } catch {
        toast.error("Failed to move photos");
        trackEvent(ANALYTICS_EVENTS.PHOTO_ROOM_REASSIGN_FAILED, analyticsPayload);
      } finally {
        setIsMoving(false);
      }
    },
    [activeEstimateId, ANALYTICS_EVENTS, buildAnalyticsPayload, reassignResponsiveNotePhotos, trackEvent, updateEstimate],
  );

  const handleMoveSelectedToRoom = useCallback(
    async (targetRoomId: string, targetRoomName: string) => {
      await movePhotosToRoom(selectedPhotos, targetRoomId, targetRoomName);
    },
    [movePhotosToRoom, selectedPhotos],
  );

  // The selection is kept after downloading: nothing moved on screen, and users often follow up
  // with another action on the same photos.
  const runDownload = useCallback(
    async (photos: PhotoGalleryItem[], scope: "selected" | "all") => {
      const zipFileName = estimation?.projectName ? `${estimation.projectName} - Photos.zip` : `photos-${activeEstimateId}.zip`;

      try {
        setActiveDownload(scope);
        const { downloadedCount, failedCount } = await downloadPhotos(photos, zipFileName, downloadFileNames);

        if (downloadedCount === 0) {
          toast.error("Failed to download photos");
        } else if (failedCount > 0) {
          toast.success(`Downloaded ${downloadedCount} of ${downloadedCount + failedCount} photos. Please try the rest again.`);
        } else {
          toast.success(`Downloaded ${downloadedCount} photo${downloadedCount === 1 ? "" : "s"}`);
        }
      } catch {
        toast.error("Failed to download photos");
      } finally {
        setActiveDownload(null);
      }
    },
    [activeEstimateId, downloadFileNames, estimation?.projectName],
  );

  const handleDownloadSelected = useCallback(() => runDownload(selectedPhotos, "selected"), [runDownload, selectedPhotos]);

  const handleDownloadAll = useCallback(
    () => downloadablePhotos.length > 0 && runDownload(downloadablePhotos, "all"),
    [downloadablePhotos, runDownload],
  );

  const handleApplyLabelsToSelection = useCallback(
    async (labels: ResponsiveNotePhotoLabel[]) => {
      const labelablePhotos = selectedPhotos.filter(isLabelablePhoto);
      const skippedCount = selectedPhotos.length - labelablePhotos.length;

      if (!activeEstimateId || labelablePhotos.length === 0) {
        return;
      }

      const analyticsPayload = {
        estimate_id: activeEstimateId,
        photo_count: labelablePhotos.length,
        label_count: labels.length,
        skipped_legacy_count: skippedCount,
      };
      trackEvent(ANALYTICS_EVENTS.PHOTO_LABELS_APPLY_INITIATED, analyticsPayload);

      // Same rationale as movePhotosToRoom: the mutation patches the cache optimistically, so the
      // toolbar can dismiss straight away without the labels flickering.
      setSelectedIds(new Set());

      const photoRefs = labelablePhotos.map((photo) => ({
        noteId: photo.responsiveNotePhoto!.noteId,
        photoId: photo.responsiveNotePhoto!.id,
      }));
      const skippedSuffix = skippedCount > 0 ? ` ${skippedCount} uploaded photo${skippedCount === 1 ? "" : "s"} skipped.` : "";

      // Chunked because the API caps a single call at MAX_PHOTOS_PER_LABEL_APPLY.
      let labelledCount = 0;
      try {
        for (let start = 0; start < photoRefs.length; start += MAX_PHOTOS_PER_LABEL_APPLY) {
          const chunk = photoRefs.slice(start, start + MAX_PHOTOS_PER_LABEL_APPLY);
          await applyPhotoLabels({ estimateId: activeEstimateId, labels, mode: "replace", photos: chunk });
          labelledCount += chunk.length;
        }

        toast.success(`Labelled ${labelledCount} photo${labelledCount === 1 ? "" : "s"}.${skippedSuffix}`);
        trackEvent(ANALYTICS_EVENTS.PHOTO_LABELS_APPLIED, analyticsPayload);
      } catch {
        // A later chunk can fail after earlier ones committed; say what actually landed, because the
        // selection is already cleared and the user has nothing left to inspect.
        toast.error(
          labelledCount > 0
            ? `Only labelled ${labelledCount} of ${photoRefs.length} photos. Please try the rest again.`
            : "Failed to apply labels",
        );
        trackEvent(ANALYTICS_EVENTS.PHOTO_LABELS_APPLY_FAILED, { ...analyticsPayload, photo_count: labelledCount });
      }
    },
    [activeEstimateId, ANALYTICS_EVENTS, applyPhotoLabels, selectedPhotos, trackEvent],
  );

  // Single-photo editing from the preview. No toast on success and no confirm on removal: the chip
  // appearing or vanishing is the feedback, and the mutation patches the cache before it awaits.
  const handleEditPhotoLabel = useCallback(
    async (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel, mode: "add" | "remove") => {
      if (!activeEstimateId || !isLabelablePhoto(photo)) {
        return;
      }

      const analyticsPayload = { estimate_id: activeEstimateId, photo_count: 1, label_count: 1 };

      try {
        await applyPhotoLabels({
          estimateId: activeEstimateId,
          labels: [label],
          mode,
          photos: [{ noteId: photo.responsiveNotePhoto!.noteId, photoId: photo.responsiveNotePhoto!.id }],
        });
        trackEvent(
          mode === "add" ? ANALYTICS_EVENTS.PHOTO_LABELS_APPLIED : ANALYTICS_EVENTS.PHOTO_LABELS_REMOVED,
          mode === "add" ? { ...analyticsPayload, skipped_legacy_count: 0 } : analyticsPayload,
        );
      } catch {
        toast.error(mode === "add" ? "Failed to add tag." : "Failed to remove tag.");
        trackEvent(ANALYTICS_EVENTS.PHOTO_LABELS_APPLY_FAILED, { ...analyticsPayload, photo_count: 0, skipped_legacy_count: 0 });
      }
    },
    [activeEstimateId, ANALYTICS_EVENTS, applyPhotoLabels, trackEvent],
  );

  const handleAddPhotoLabel = useCallback(
    (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void handleEditPhotoLabel(photo, label, "add"),
    [handleEditPhotoLabel],
  );

  const handleRemovePhotoLabel = useCallback(
    (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void handleEditPhotoLabel(photo, label, "remove"),
    [handleEditPhotoLabel],
  );

  // The apply endpoint always writes to the caller's own org, so an internal user viewing
  // someone else's estimate would otherwise label photos in the wrong place.
  const isLabelEditingBlocked = Boolean(getInternalOrgOverride());

  const labelDisabledReason = useMemo(() => {
    if (isLabelEditingBlocked) {
      return "Labels can't be applied while viewing another organization.";
    }
    if (selectedPhotos.length === 0 || selectedPhotos.some(isLabelablePhoto)) {
      return undefined;
    }
    return "Labels can only be applied to photos captured in a note.";
  }, [isLabelEditingBlocked, selectedPhotos]);

  const selectedPhotosRoomId = useMemo(() => {
    const roomIds = new Set(selectedPhotos.map((photo) => photo.roomId));
    return roomIds.size === 1 ? [...roomIds][0] : undefined;
  }, [selectedPhotos]);

  const photosToDelete = useMemo(() => selectedPhotos.slice(0, MAX_PHOTOS_PER_DELETE), [selectedPhotos]);
  const deleteOverflowCount = selectedPhotos.length - photosToDelete.length;

  // Same reason as labels: the endpoint always writes to the caller's own org, so an internal user
  // viewing someone else's estimate would be deleting against the wrong one. Additionally, the
  // deletion is disabled if the deletion is pending.
  const isDeleteDisabled = isLabelEditingBlocked || isDeletePending;

  const isDownloadingAll = activeDownload === "all";

  const handleConfirmDeleteSelection = useCallback(async () => {
    if (!activeEstimateId || photosToDelete.length === 0) {
      return;
    }

    const deletedCount = photosToDelete.length;
    const analyticsPayload = {
      estimate_id: activeEstimateId,
      photo_count: deletedCount,
      over_limit_count: deleteOverflowCount,
    };
    trackEvent(ANALYTICS_EVENTS.PHOTO_DELETE_INITIATED, analyticsPayload);

    setIsDeleteDialogOpen(false);

    // Anything over the cap stays selected so a large selection can be worked through by pressing
    // delete again. The mutation patches the cache optimistically, so the grid updates right away.
    const deletedGalleryIds = new Set(photosToDelete.map((photo) => photo.galleryId));
    const previousSelection = selectedIds;
    setSelectedIds(new Set([...selectedIds].filter((id) => !deletedGalleryIds.has(id))));

    try {
      await deleteResponsiveNotePhotos({
        estimateId: activeEstimateId,
        photos: photosToDelete.map((photo) => ({
          noteId: photo.responsiveNotePhoto!.noteId,
          photoId: photo.responsiveNotePhoto!.id,
        })),
      });

      const overflowSuffix = deleteOverflowCount > 0 ? ` ${deleteOverflowCount} more still selected.` : "";
      toast.success(`Deleted ${deletedCount} photo${deletedCount === 1 ? "" : "s"}.${overflowSuffix}`);
      trackEvent(ANALYTICS_EVENTS.PHOTO_DELETED, analyticsPayload);
    } catch {
      // Put the selection back so retrying is one click, matching the mutation's cache rollback.
      setSelectedIds(previousSelection);
      toast.error("Failed to delete photos");
      trackEvent(ANALYTICS_EVENTS.PHOTO_DELETE_FAILED, { ...analyticsPayload, photo_count: 0 });
    }
  }, [activeEstimateId, ANALYTICS_EVENTS, deleteOverflowCount, deleteResponsiveNotePhotos, photosToDelete, selectedIds, trackEvent]);

  const predictionsHaveBoxes = lineItemDetail?.lineItem.predictions?.some((pred) => boxForPrediction(pred) !== undefined);
  const lineItemFiles = useMemo(
    () => getLineItemFiles(filesSorted, Boolean(predictionsHaveBoxes), currentRoom?.id),
    [filesSorted, predictionsHaveBoxes, currentRoom?.id],
  );
  const activeGalleryItems = lineItemDetail ? normalizeFilePhotos(lineItemFiles) : photosTabItems;
  const isMovePending = isReassignPending || isMoving;

  const closePhotoPreview = useCallback(() => selectPhoto(undefined), [selectPhoto]);
  const exitFullscreen = useCallback(() => {
    selectPhoto(undefined);
    setIsFullscreen(false);
  }, [selectPhoto]);

  if (!lineItemDetail && activeGalleryItems.length === 0 && isResponsiveNotePhotosLoading) {
    return (
      <EmptyTabState
        icon={<Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />}
        title="Loading photos"
        description="Loading photos for this estimate."
      />
    );
  }

  if (activeGalleryItems.length === 0) {
    const responsiveNotePhotoError = !lineItemDetail && hasResponsiveNotePhotosError;
    return (
      <EmptyTabState
        icon={<Image className="text-muted-foreground h-6 w-6" />}
        title={responsiveNotePhotoError ? "Could not load photos" : "No photos"}
        description={
          responsiveNotePhotoError ? "Could not load responsive note photos." : "No photos have been uploaded for this estimate."
        }
      />
    );
  }

  if (!lineItemDetail) {
    const previewOverlay = isFullscreen ? fullscreenPreviewOverlay : photoPreviewOverlayRef?.current;
    const selectionToolbar = selectedPhotoId === undefined && (
      <PhotoSelectionToolbar
        estimateId={activeEstimateId}
        selectedCount={selectedIds.size}
        roomOptions={roomOptions}
        currentRoomId={selectedPhotosRoomId}
        onMoveToRoom={handleMoveSelectedToRoom}
        onClear={() => setSelectedIds(new Set())}
        onDownload={handleDownloadSelected}
        onDelete={() => setIsDeleteDialogOpen(true)}
        isDeletePending={isDeletePending}
        isDeleteDisabled={isDeleteDisabled}
        isDownloadPending={activeDownload === "selected"}
        isMoveDisabled={roomOptions.length === 0}
        isMovePending={isMovePending}
        onApplyLabels={handleApplyLabelsToSelection}
        isLabelPending={isApplyLabelsPending}
        labelDisabledReason={labelDisabledReason}
      />
    );
    const renderGallery = () => (
      <PhotoGallery
        groups={displayGroups}
        onSelect={selectPhoto}
        hideRoomNames={hideRoomNames}
        currentRoomId={currentRoom?.id}
        selectedIds={selectedIds}
        onToggleSelect={togglePhotoSelection}
        onSelectAllInGroup={handleSelectAllInGroup}
        showDateSubheaders={!hideRoomNames}
      />
    );

    return (
      <>
        {hasResponsiveNotePhotosError && files.length > 0 && (
          <div className="px-4 pt-3 text-xs text-red-600">Could not load responsive note photos.</div>
        )}
        {(canExpand || !!handleDownloadAll) && (
          <div className="flex justify-end gap-2 p-1">
            {canExpand && (
              <Button variant="ghost" onClick={() => setIsFullscreen(true)}>
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                Full screen
              </Button>
            )}
            {!!handleDownloadAll && (
              <Button variant="secondary" disabled={isDownloadingAll} onClick={handleDownloadAll}>
                {isDownloadingAll ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Download all
              </Button>
            )}
          </div>
        )}
        <div>
          {renderGallery()}
          {selectedPhotoId !== undefined &&
            previewOverlay &&
            createPortal(
              <div className="pointer-events-auto h-full w-full overflow-y-auto bg-white">
                <PhotoPreview
                  photos={previewPhotos}
                  onClose={closePhotoPreview}
                  selectedPhotoId={selectedPhotoId}
                  estimateId={activeEstimateId}
                  onAddLabel={isLabelEditingBlocked ? undefined : handleAddPhotoLabel}
                  onRemoveLabel={isLabelEditingBlocked ? undefined : handleRemovePhotoLabel}
                  variant={isFullscreen ? "fullscreen" : "sidebar"}
                />
              </div>,
              previewOverlay,
            )}
        </div>
        {isFullscreen && (
          <PhotoGalleryFullscreen
            photoCount={photosTabItems.length}
            isPreviewOpen={selectedPhotoId !== undefined}
            onClosePreview={closePhotoPreview}
            onExit={exitFullscreen}
            previewOverlayRef={setFullscreenPreviewOverlay}
          >
            {renderGallery()}
            {selectionToolbar}
          </PhotoGalleryFullscreen>
        )}
        {!isFullscreen && selectionToolbar}
        <DeletePhotosDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirm={() => void handleConfirmDeleteSelection()}
          photoCount={photosToDelete.length}
          overflowCount={deleteOverflowCount}
        />
      </>
    );
  }

  if (!predictionsHaveBoxes) {
    const filesToShow = lineItemFiles;
    const fileGalleryItems = normalizeFilePhotos(filesToShow);
    const fileGroups = buildPhotoGalleryGroups({
      estimatePhotos: fileGalleryItems,
      responsiveNotePhotos: [],
      rooms,
    });

    return selectedPhotoId === undefined ? (
      <PhotoGallery groups={fileGroups} onSelect={selectPhoto} hideRoomNames={hideRoomNames} currentRoomId={currentRoom?.id} />
    ) : (
      <PhotoPreview
        photos={fileGalleryItems}
        onClose={() => selectPhoto(undefined)}
        selectedPhotoId={selectedPhotoId}
        predictions={lineItemDetail?.lineItem.predictions}
      />
    );
  }

  const filesToShow = lineItemFiles;
  const fileGalleryItems = normalizeFilePhotos(filesToShow);
  return selectedPhotoId === undefined ? (
    <RoomPhotoGallery
      photos={filesToShow}
      onSelect={(uploadId) => selectPhoto(fileGalleryItems.find((photo) => photo.predictionSourceUploadId === uploadId))}
      estimateId={estimation?.estimateId || ""}
      predictions={lineItemDetail?.lineItem.predictions}
    />
  ) : (
    <PhotoPreview
      photos={fileGalleryItems}
      onClose={() => selectPhoto(undefined)}
      selectedPhotoId={selectedPhotoId}
      predictions={lineItemDetail?.lineItem.predictions}
      includePredictionsInCaption={true}
    />
  );
};

function normalizeFilePhotos(files: File[]): PhotoGalleryItem[] {
  return files.map(normalizeEstimateFilePhoto).filter(isPhotoGalleryItem);
}

function getLineItemFiles(files: File[], predictionsHaveBoxes: boolean, currentRoomId?: string): File[] {
  const filesToSlice =
    predictionsHaveBoxes && currentRoomId ? files.filter((file) => getEstimateFileRoomId(file) === currentRoomId) : files;
  return filesToSlice.slice(0, predictionsHaveBoxes ? 6 : 3);
}
